import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EMP_DOMAIN } from "../../services/config";

const FLOW_DESTINATIONS = {
  verifyEmail: "/verify-email",
  resetPassword: "/updatepassword",
  verifyAndChangeEmail: "/verify-new-email",
  recoverEmail: "/verify-email",
};

const MOBILE_PATHS = {
  verifyEmail: "/verify-email",
  resetPassword: "/updatepassword",
  verifyAndChangeEmail: "/verifyEmpEmail",
  recoverEmail: "/verify-email",
};

const MODE_TO_FLOW = {
  verifyEmail: "verifyEmail",
  resetPassword: "resetPassword",
  verifyAndChangeEmail: "verifyAndChangeEmail",
  recoverEmail: "recoverEmail",
};

const CLIENT_TEAM_MARKERS = new Set(["clientteam", "client team"]);

function resolveDestination({ mode, flow, continueUrl }) {
  const flowKey = flow || MODE_TO_FLOW[mode] || null;
  if (flowKey && FLOW_DESTINATIONS[flowKey]) {
    return FLOW_DESTINATIONS[flowKey];
  }

  try {
    if (continueUrl) {
      const path = new URL(continueUrl).pathname.toLowerCase();
      if (path === "/updatepassword") return "/updatepassword";
      if (path === "/verify-email") return "/verify-email";
      if (path === "/verify-new-email" || path === "/verifyempemail") {
        return "/verify-new-email";
      }
      if (
        path === "/firebase-action-handler" &&
        mode &&
        FLOW_DESTINATIONS[MODE_TO_FLOW[mode]]
      ) {
        return FLOW_DESTINATIONS[MODE_TO_FLOW[mode]];
      }
    }
  } catch {
    // continueUrl may be missing or malformed
  }

  if (mode === "resetPassword") return "/updatepassword";
  if (mode === "verifyEmail" || mode === "recoverEmail") return "/verify-email";
  if (mode === "verifyAndChangeEmail") return "/verify-new-email";

  return null;
}

const FirebaseActionHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const mode = searchParams.get("mode");
    const flow = searchParams.get("flow");
    const oobCode = searchParams.get("oobCode");
    const apiKey = searchParams.get("apiKey");
    const continueUrl = searchParams.get("continueUrl");
    const role = searchParams.get("role");
    const email = searchParams.get("email");
    const employeerole = searchParams.get("employeerole");
    const uid = searchParams.get("uid");
    const userId = searchParams.get("userId");
    const setup = searchParams.get("setup");

    if (!oobCode) {
      navigate("/login", { replace: true });
      return;
    }

    const resolvedMode = mode || flow || null;
    if (!resolvedMode) {
      navigate("/login", { replace: true });
      return;
    }

    let continueEmail = "";
    let continueUid = "";
    let continueUserId = "";
    let continueSetup = "";
    try {
      if (continueUrl) {
        const cu = new URL(continueUrl);
        continueEmail = cu.searchParams.get("email") || "";
        continueUid = cu.searchParams.get("uid") || "";
        continueUserId = cu.searchParams.get("userId") || "";
        continueSetup = cu.searchParams.get("setup") || "";
      }
    } catch {
      // continueUrl may be missing or malformed
    }

    const resolvedSetup = setup || continueSetup;
    const flowKey = flow || MODE_TO_FLOW[resolvedMode] || "resetPassword";
    const mobilePath = MOBILE_PATHS[flowKey] || "/updatepassword";
    const empParams = new URLSearchParams();
    empParams.set("mode", resolvedMode);
    empParams.set("oobCode", oobCode);
    if (apiKey) empParams.set("apiKey", apiKey);
    empParams.set("lang", "en");
    if (mobilePath === "/updatepassword" && resolvedSetup) {
      empParams.set("setup", String(resolvedSetup).trim());
    }
    const empUrl = `https://${EMP_DOMAIN}${mobilePath}?${empParams.toString()}`;

    const roleMarker = String(searchParams.get("role") || "")
      .trim()
      .toLowerCase();
    const employeeRoleMarker = String(searchParams.get("employeerole") || "")
      .trim()
      .toLowerCase();
    const isClientTeam =
      CLIENT_TEAM_MARKERS.has(employeeRoleMarker) ||
      CLIENT_TEAM_MARKERS.has(roleMarker);

    // Legacy CMS links only — new Client Team emails go direct to emp from backend.
    if (isClientTeam) {
      window.location.replace(empUrl);
      return;
    }

    const query = new URLSearchParams({
      oobCode,
      ...(apiKey && { apiKey }),
      ...(role && { role }),
      ...(email && { email }),
      ...(employeerole && { employeerole }),
      ...(continueEmail && !email && { email: continueEmail }),
      ...(uid && { uid }),
      ...(continueUid && !uid && { uid: continueUid }),
      ...(userId && { userId }),
      ...(continueUserId && !userId && { userId: continueUserId }),
      ...(resolvedSetup && { setup: resolvedSetup }),
      ...(flow && { flow }),
    }).toString();

    const destination = resolveDestination({
      mode: resolvedMode,
      flow,
      continueUrl,
    });

    if (destination) {
      navigate(`${destination}?${query}`, { replace: true });
      return;
    }

    window.location.replace(empUrl);
  }, [navigate, searchParams]);

  return null;
};

export default FirebaseActionHandler;
